import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { companiesByIdSelector } from '@/domain/companies';
import { saveBackSearchUrlAction } from '@/domain/env';
import { isOpenedSelector, toggleEventPanelAction } from '@/domain/events';
import type { TrailLink } from '@/domain/events/schemas';
import { setPaymentNavigationAction } from '@/domain/payments/actions';
import { querySelector } from '@/domain/router';
import { useApiToken } from '@/lib/apiTokenKeeper';

import EventsTrailPanel from '@/components/mui/Layouts/components/EventsHistoryPanel/EventsTrailPanel';
import { EVENTS_HISTORY_DRAWER_WIDTH } from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsHistoryDrawer';
import PaymentPreview from '@/pages/dokkaPay/pages/company/components/PaymentPreview';
import {
  getPaymentPreviewSrc,
  getPaymentUrl,
} from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/helpers';

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

interface PayEventsTrailProps {
  paymentId?: string;
}

// As in the AP trail, an event opens a preview; its Open closes the trail and navigates.
const PayEventsTrail: React.FC<PayEventsTrailProps> = ({ paymentId }) => {
  const dispatch = useDispatch();
  const routeParams = useParams();
  const { companyId = '' } = routeParams;
  const apiToken = useApiToken();
  const isOpened = useSelector(isOpenedSelector);
  const query = useSelector(querySelector);
  const dateFormat: string =
    useSelector((state) => companiesByIdSelector(state).getIn([companyId, 'dateFormat'])) || DEFAULT_DATE_FORMAT;
  const [previewId, setPreviewId] = useState<string | null>(null);

  const [wasOpened, setWasOpened] = useState(isOpened);
  if (wasOpened !== isOpened) {
    setWasOpened(isOpened);
    if (!isOpened) setPreviewId(null);
  }

  const closeTrail = useCallback(() => dispatch(toggleEventPanelAction()), [dispatch]);
  const closePreview = useCallback(() => setPreviewId(null), []);

  // Same bookkeeping as opening a payment from the grid, so Back and prev/next work on the payment page.
  const openPayment = useCallback(() => {
    dispatch(saveBackSearchUrlAction({ query, routeParams }));
    dispatch(setPaymentNavigationAction({ search: { query, params: routeParams }, isSetRequest: true }));
    closeTrail();
  }, [dispatch, query, routeParams, closeTrail]);

  const getEventAction = useCallback(
    (link: TrailLink) => (link.paymentId !== paymentId ? () => setPreviewId(link.paymentId) : null),
    [paymentId],
  );

  const preview = useMemo(
    () =>
      previewId && companyId
        ? { src: getPaymentPreviewSrc(companyId, previewId), to: getPaymentUrl(companyId, previewId) }
        : null,
    [companyId, previewId],
  );

  return (
    <EventsTrailPanel
      source="pay"
      entityId={paymentId}
      isOpened={isOpened}
      onClose={closeTrail}
      dateFormat={dateFormat}
      getEventAction={getEventAction}
    >
      {preview && apiToken !== null ? (
        <PaymentPreview
          src={preview.src}
          to={preview.to}
          rotationAngle={0}
          onClick={openPayment}
          close={closePreview}
          xGap={EVENTS_HISTORY_DRAWER_WIDTH}
        />
      ) : null}
    </EventsTrailPanel>
  );
};

export default PayEventsTrail;
